import { ArrowDownIcon, ArrowUpIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FormWrapper } from '@/form-control'
import { SelectField, ToggleField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IRoadmapMilestone } from '@/MODELS/roadmapMilestone.model'
import { IRoadmapSequence } from '@/MODELS/roadmapSequence.model'

import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'

const AGE_GROUP_OPTIONS = [
    { value: '0-3', text: '0-3 years' },
    { value: '4-5', text: '4-5 years' },
    { value: '6-12', text: '6-12 years' },
    { value: '13-17', text: '13-17 years' },
    { value: '18+', text: '18+ years' },
]

const DIAGNOSIS_STATUS_OPTIONS = [
    { value: 'pre-diagnosis', text: 'Pre-diagnosis' },
    { value: 'in evaluation process', text: 'In evaluation process' },
    { value: 'diagnosed recently', text: 'Diagnosed recently' },
    { value: 'diagnosed over a year ago', text: 'Diagnosed over a year ago' },
]

export default function AdminRoadmapSequenceForm(props: { id?: string }) {
    const params = useParams()
    const id = props?.id ?? params?.id ?? 'new'
    const isNew = id === 'new'

    const [milestones] = useResource<IRoadmapMilestone[]>(ROUTES.admin.roadmapMilestone)
    const [sequences] = useResource<IRoadmapSequence[]>(ROUTES.admin.roadmapSequence)
    const allMilestones = milestones ?? []
    const activeMilestones = allMilestones
        .filter((m) => m.isActive)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const milestonesByKey = useMemo(() => {
        const map: Record<string, IRoadmapMilestone> = {}
        for (const m of allMilestones) map[m.key] = m
        return map
    }, [allMilestones])

    return (
        <>
            <AdminPageHeader heading={isNew ? 'Create New Sequence' : 'Edit Sequence'} />
            <FormWrapper<IRoadmapSequence>
                endpoint={ROUTES.admin.roadmapSequence}
                id={id}
                redirectAfterSubmit='/admin/roadmap-sequences'
                defaultValues={{ isActive: true, milestoneKeys: [] }}
                additionalSubmissionRowContent={
                    <div className='flex items-center justify-between w-full gap-x-2 h-fit'>
                        <Button.Back />
                    </div>
                }
            >
                {(f, { formValues, setFormValues }) => {
                    const milestoneKeys = (formValues.milestoneKeys as string[]) ?? []

                    const existingSequence = useMemo(() => {
                        if (!isNew || !sequences || !formValues.ageGroup || !formValues.diagnosisStatus) return null
                        return sequences.find(
                            (s) => s.ageGroup === formValues.ageGroup && s.diagnosisStatus === formValues.diagnosisStatus
                        ) || null
                    }, [sequences, formValues.ageGroup, formValues.diagnosisStatus, isNew])

                    const moveUp = (index: number) => {
                        if (index === 0) return
                        const arr = [...milestoneKeys]
                        ;[arr[index - 1], arr[index]] = [arr[index], arr[index - 1]]
                        setFormValues({ ...formValues, milestoneKeys: arr })
                    }

                    const moveDown = (index: number) => {
                        if (index >= milestoneKeys.length - 1) return
                        const arr = [...milestoneKeys]
                        ;[arr[index], arr[index + 1]] = [arr[index + 1], arr[index]]
                        setFormValues({ ...formValues, milestoneKeys: arr })
                    }

                    const removeKey = (index: number) => {
                        setFormValues({
                            ...formValues,
                            milestoneKeys: milestoneKeys.filter((_, i) => i !== index),
                        })
                    }

                    const addMilestone = (key: string) => {
                        if (!key || milestoneKeys.includes(key)) return
                        setFormValues({
                            ...formValues,
                            milestoneKeys: [...milestoneKeys, key],
                        })
                    }

                    // Milestones available to add (not already in the list)
                    const availableToAdd = activeMilestones.filter((m) => !milestoneKeys.includes(m.key))
                    const deactivatedKeys = milestoneKeys.filter((key) => {
                        const m = milestonesByKey[key]
                        return m && !m.isActive
                    })

                    return (
                        <div className='border rounded-xl mt-8'>
                            <section className='bg-white rounded-xl shadow-sm border-gray-200 overflow-hidden'>
                                <div className='p-6 space-y-6'>
                                    <div>
                                        <h3 className='text-sm font-semibold text-gray-900 mb-3'>Demographics</h3>
                                        {isNew ? (
                                            <div className='grid lg:grid-cols-2 gap-6'>
                                                <SelectField
                                                    {...f('diagnosisStatus')}
                                                    label='Diagnosis Status'
                                                    options={DIAGNOSIS_STATUS_OPTIONS}
                                                />
                                                <SelectField
                                                    {...f('ageGroup')}
                                                    label='Age Group'
                                                    options={AGE_GROUP_OPTIONS}
                                                />
                                            </div>
                                        ) : (
                                            <div className='grid lg:grid-cols-2 gap-6'>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>Diagnosis Status</label>
                                                    <p className='text-sm text-gray-900 py-1'>{formValues.diagnosisStatus}</p>
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>Age Group</label>
                                                    <p className='text-sm text-gray-900 py-1'>{formValues.ageGroup}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {existingSequence && (
                                        <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                            <p className='text-sm text-amber-800'>
                                                {existingSequence.isActive
                                                    ? 'An active sequence already exists for this combination. '
                                                    : 'An inactive sequence already exists for this combination. '}
                                                <Link
                                                    to={`/admin/roadmap-sequence/${existingSequence._id}`}
                                                    className='font-medium underline hover:text-amber-900'
                                                >
                                                    Edit the existing sequence
                                                </Link>
                                                {!existingSequence.isActive && ' and reactivate it'} instead of creating a new one.
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <ToggleField {...f('isActive')} label='Active' />
                                        <p className='text-xs text-gray-500 mt-1'>
                                            Inactive sequences are excluded from roadmap generation. If no active sequence exists for a user's age and diagnosis status, they will receive a minimal fallback roadmap.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className='text-sm font-semibold text-gray-900 mb-3'>Milestone Sequence</h3>
                                        <p className='text-xs text-gray-500 mb-3'>
                                            Define the ordered list of milestones a user will see in their roadmap. The first milestone is the user's starting point.
                                        </p>

                                        {deactivatedKeys.length > 0 && (
                                            <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4'>
                                                <div className='flex items-start gap-2'>
                                                    <ExclamationTriangleIcon className='w-4 h-4 text-amber-600 shrink-0 mt-0.5' />
                                                    <p className='text-sm text-amber-800'>
                                                        This sequence contains {deactivatedKeys.length} inactive milestone{deactivatedKeys.length > 1 ? 's' : ''} that will be skipped during roadmap generation: <span className='font-medium'>{deactivatedKeys.join(', ')}</span>. Remove {deactivatedKeys.length > 1 ? 'them' : 'it'} or reactivate {deactivatedKeys.length > 1 ? 'them' : 'it'} in Roadmap Milestones.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Add milestone dropdown */}
                                        {availableToAdd.length > 0 && (
                                            <div className='mb-4'>
                                                <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                    Add Milestone
                                                </label>
                                                <div className='flex gap-2'>
                                                    <select
                                                        id='add-milestone-select'
                                                        className='flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                                        defaultValue=''
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                addMilestone(e.target.value)
                                                                e.target.value = ''
                                                            }
                                                        }}
                                                    >
                                                        <option value='' disabled>
                                                            Select a milestone to add...
                                                        </option>
                                                        {availableToAdd.map((m) => (
                                                            <option key={m.key} value={m.key}>
                                                                {m.title} ({m.key})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {/* Ordered milestone list */}
                                        {milestoneKeys.length > 0 && (
                                            <div>
                                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                                    Milestone Order ({milestoneKeys.length} milestones)
                                                </label>
                                                <div className='space-y-2'>
                                                    {milestoneKeys.map((key, index) => {
                                                        const milestone = milestonesByKey[key]
                                                        const isInactive = milestone && !milestone.isActive
                                                        return (
                                                            <div
                                                                key={key}
                                                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                                                    isInactive
                                                                        ? 'border-amber-300 bg-amber-50'
                                                                        : 'border-neutral-200 bg-neutral-50'
                                                                }`}
                                                            >
                                                                <span className='text-sm font-medium text-neutral-400 w-6 text-center'>
                                                                    {index + 1}
                                                                </span>
                                                                <span className={`flex-1 text-sm font-medium ${isInactive ? 'text-amber-700' : ''}`}>
                                                                    {milestone?.title ?? key}
                                                                    {isInactive && (
                                                                        <span className='ml-2 text-xs font-normal text-amber-600'>(inactive)</span>
                                                                    )}
                                                                </span>
                                                                <span className='text-xs text-gray-400 font-mono'>
                                                                    {key}
                                                                </span>
                                                                <div className='flex gap-1'>
                                                                    <button
                                                                        type='button'
                                                                        disabled={index === 0}
                                                                        onClick={() => moveUp(index)}
                                                                        className='p-1 rounded hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed'
                                                                    >
                                                                        <ArrowUpIcon className='w-4 h-4' />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        disabled={index === milestoneKeys.length - 1}
                                                                        onClick={() => moveDown(index)}
                                                                        className='p-1 rounded hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed'
                                                                    >
                                                                        <ArrowDownIcon className='w-4 h-4' />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => removeKey(index)}
                                                                        className='p-1 rounded hover:bg-red-100 text-red-500'
                                                                    >
                                                                        <XMarkIcon className='w-4 h-4' />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {milestoneKeys.length === 0 && (
                                            <div className='p-4 rounded-lg border-2 border-dashed border-neutral-300 text-center'>
                                                <p className='text-sm text-neutral-500'>
                                                    No milestones added yet. Use the dropdown above to add milestones to this sequence.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>
                        </div>
                    )
                }}
            </FormWrapper>
        </>
    )
}
